<script setup lang="ts">
import { type ShallowRef, computed, shallowRef, watchEffect } from 'vue'
import { TabsTrigger } from 'radix-vue'
import { templateRef } from '@vueuse/core'
import { ELayout, EVariant, type ITabsTriggerProps } from './types'
import useTabsTriggerConfig from '@/ui/plushComponents/VTabs/composables/useTabsTriggerConfig'
import useTabsPillTriggerConfig from '@/ui/plushComponents/VTabs/composables/useTabsPillTriggerConfig'
import { SIZES, type TIconSizesType } from '@/ui/ts/types/Sizes'

const props = withDefaults(defineProps<ITabsTriggerProps>(), {
    size: SIZES.LARGE,
    variant: EVariant.DEFAULT,
})

const emit = defineEmits<{ (e: 'focus'): void }>()

const tabsTriggerConfig: ShallowRef<Record<string, string>> = shallowRef({} as Record<string, string>)

watchEffect(() => {
    tabsTriggerConfig.value =
        props.variant === EVariant.PILL ? useTabsPillTriggerConfig(props) : useTabsTriggerConfig(props)
})

const root = templateRef<HTMLElement | null>('root')

defineExpose({
    root,
})

const iconSizes: Record<typeof SIZES.MEDIUM | typeof SIZES.LARGE, TIconSizesType> = {
    MEDIUM: 's',
    LARGE: 'm',
}

const pillMedium = computed(() => props.variant === EVariant.PILL && iconSizes[props.size] === iconSizes.MEDIUM)
</script>

<template>
    <TabsTrigger
        ref="root"
        :value="value"
        :class="[
            tabsTriggerConfig.rootClasses,
            layout === ELayout.HUG && 'shrink-0',
            layout === ELayout.FILL && 'min-w-0 flex-1',
            pillMedium && 'flex-wrap',
        ]"
        :disabled="disabled"
        @focus="emit('focus')"
    >
        <VIcon
            v-if="icon"
            :name="icon"
            :size="iconSizes[size]"
            :class="pillMedium && 'w-full'"
        />
        <div
            v-html-gpp="name"
            class="line-clamp-2"
        />
    </TabsTrigger>
</template>
